import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl,
} from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import {
  MatFormField,
  MatFormFieldAppearance,
} from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { IconName } from '@sc/data-access-assets';
import { ConfigurationService } from '@sc/data-access-configuration';
import { TranslatorService } from '@sc/data-access-translation';
import { Subject, Subscription, interval } from 'rxjs';
import { debounce } from 'rxjs/operators';
import { LabelRequiredPipe } from '../../pipes';
import { ErrorMessagesService } from '../../services';
import { PatternError } from '@sc/data-access-models';
import { UtilsValidators } from '../../validators';
import { ErrorComponent } from '../error';
import { IconComponent } from '../icon';
import { randomId } from '@sc/utils';

@Component({
  selector: 'sc-input-text',
  styleUrls: ['./input-text.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    NgClass,
    ErrorComponent,
    IconComponent,
    LabelRequiredPipe,
    MatInputModule,
    MatAutocompleteModule,
    TranslateModule,
    AsyncPipe,
  ],
  template: `
    <mat-label
      *ngIf="label && appearance === 'outline'"
      class="sc-design-text-4"
    >
      <label
        [innerHTML]="
          label
            | labelRequired
              : hasRequiredValidator(ngControl?.control) || asterisk
              : forceHighlightRequired
        "
        [attr.for]="labelId"
      ></label>
    </mat-label>
    <mat-form-field
      [appearance]="appearance"
      [ngClass]="{
        error: isError(),
        'is-left-icon': !!leftIcon,
        'is-right-icon': !!rightIcon,
        'margin-left-icon': forceShowLeftIcon
      }"
      (click)="onInputClick()"
    >
      <mat-label class="sc-design-text-4" [attr.for]="labelId">
        <p
          *ngIf="label && appearance !== 'outline'"
          [innerHTML]="
            label
              | labelRequired
                : hasRequiredValidator(ngControl?.control) || asterisk
                : forceHighlightRequired
          "
        ></p>
      </mat-label>
      <div
        *ngIf="suggestions && !!value"
        class="sr_only"
        aria-live="polite"
        aria-atomic="true"
      >
        <p [id]="helpTextId">
          {{
            suggestions.length > 0
              ? ('GlobalApplicativeConstants.accessibility.suggestionsHelpText'
                | translate: { numberSuggestions: suggestions.length })
              : ('GlobalApplicativeConstants.accessibility.noSuggestionsHelpText'
                | translate: { input: value })
          }}
        </p>
      </div>
      <input
        #mainInput
        [id]="labelId"
        [type]="type"
        [value]="value ? value : ''"
        [placeholder]="placeholder"
        [autocomplete]="autocomplete"
        [matAutocomplete]="suggestions ? auto : undefined"
        [matAutocompleteDisabled]="!suggestions"
        [disabled]="disabled"
        [pattern]="pattern"
        [attr.data-testid]="getDataTestId()"
        [attr.inputMode]="inputMode"
        [aria-describedby]="getAriaDescribedby()"
        [attr.title]="getInputTitle() | async"
        [attr.aria-required]="
          hasRequiredValidator(ngControl?.control) ? true : undefined
        "
        [attr.aria-invalid]="isError() ? true : undefined"
        [attr.aria-disabled]="disabled ? true : undefined"
        (input)="onInputValueChange($event)"
        (blur)="onTouched()"
        (focusin)="onFocusin()"
        (focusout)="onFocusout()"
        class="sc-design-text-3"
        matInput
      />
      <mat-autocomplete
        #auto="matAutocomplete"
        (optionSelected)="onOptionSelected($event?.option?.value)"
      >
        <mat-option
          *ngFor="let option of suggestions"
          [value]="option"
          [disabled]="option?.disabled"
          [id]="'mat-option-' + getSuggestionsLabel(option)"
        >
          {{ getSuggestionsLabel(option) }}
        </mat-option>
      </mat-autocomplete>
      <mat-hint [id]="hintId" class="sc-design-text-4" *ngIf="!!hint"
        ><p [innerHTML]="hint"></p
      ></mat-hint>
    </mat-form-field>
    <sc-error
      *ngIf="displayError()"
      [id]="errorId"
      [ngClass]="{
        outline: appearance === 'outline',
        'sc-conf-error-on-input-focus': focus
      }"
      [error]="getError()"
      [displayCondition]="displayError()"
    ></sc-error>
    <button
      *ngIf="rightIcon && clickableIcon"
      type="button"
      (click)="onRightIconClick($event); onInputClick()"
      class="accessibility-icon clickable-icon right-icon"
      [ngClass]="{
        'has-value': value,
        outline: appearance === 'outline',
        hasError: displayError()
      }"
    >
      <span class="sr_only">{{ iconAccesibilityText }}</span>
      <sc-icon [name]="rightIcon" aria-hidden="true"></sc-icon>
    </button>
    <sc-icon
      *ngIf="rightIcon && !clickableIcon"
      class="accessibility-icon right-icon"
      [ngClass]="{
        'has-value': value,
        outline: appearance === 'outline',
        hasError: displayError()
      }"
      [name]="rightIcon"
      aria-hidden="true"
    ></sc-icon>
    <sc-icon
      *ngIf="isError()"
      class="accessibility-icon error-icon"
      [ngClass]="{
        'has-right-icon': !!rightIcon
      }"
      name="alert"
      aria-hidden="true"
    ></sc-icon>
    <button
      *ngIf="((leftIcon && showLeftIcon) || forceShowLeftIcon) && clickableIcon"
      type="button"
      (click)="onLeftIconClick(); onInputClick()"
      class="accessibility-icon clickable-icon left-icon"
      [ngClass]="{ hasError: displayError() }"
    >
      <span class="sr_only">{{ iconAccesibilityText }}</span>
      <sc-icon [name]="leftIcon" aria-hidden="true"></sc-icon>
    </button>
    <sc-icon
      *ngIf="
        ((leftIcon && showLeftIcon) || forceShowLeftIcon) && !clickableIcon
      "
      class="accessibility-icon left-icon"
      [ngClass]="{ hasError: displayError() || isError() }"
      [name]="leftIcon"
      aria-hidden="true"
    ></sc-icon>
  `,
})
export class InputTextComponent
  implements ControlValueAccessor, OnInit, OnDestroy, AfterViewInit
{
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTriggerElement: MatAutocompleteTrigger;

  @ViewChild('mainInput')
  mainInputElement: ElementRef<HTMLInputElement>;

  @ViewChild('auto') autocompleteElement: MatAutocomplete;

  @ViewChild(MatFormField) formField: MatFormField;

  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() type = 'text';
  @Input() placeholder: string;
  @Input() value: string;
  @Input() label: string;
  @Input() autocomplete: HTMLInputElement['autocomplete'] = 'off';
  @Input() forceDisplayError = false;
  @Input() suggestions: any[];
  @Input() suggestionsLabelName: string;
  @Input() suggestionLabelFunc;
  @Input() scrollToSuggestion: any;
  @Input() updateSuggestionDebounceTime = 250;
  @Input() forceOptionSelectedOnEnter = true;
  @Input() rightIcon: IconName;
  @Input() leftIcon: IconName;
  @Input() iconAccesibilityText: string;
  @Input() errorPattern: PatternError;
  @Input() hint: string;
  @Input() clickableIcon = false;
  @Input() forceShowLeftIcon = false;
  @Input() asterisk = false;
  @Input() pattern: string;
  @Input() inputMode: string;
  @Input() forceHighlightRequired: boolean;
  @Input() ariaDescribedby = '';
  @Input() dataTestId?: string;

  @Input() set disabled(newDisabled: boolean) {
    this.setDisabled(newDisabled);
    if (newDisabled) {
      this.ngControl?.control?.disable();
    } else {
      this.ngControl?.control?.enable();
    }
  }
  get disabled(): boolean {
    return this._disabled;
  }
  private _disabled: boolean;

  @Output() updateSuggestions = new EventEmitter<string>();
  @Output() selectSuggestion = new EventEmitter<any>();

  @Output() leftIconClick = new EventEmitter<string>();
  @Output() rightIconClick = new EventEmitter<string>();

  focus: boolean;
  valueChangesSubscription: Subscription;
  suggestionSearchSubscription: Subscription;
  suggestionSearch$ = new Subject<string>();
  showLeftIcon = false;
  errorId: string;
  hintId: string;
  labelId: string;
  helpTextId: string;
  autocompleteId: string;

  onChange: any = () => {};
  onTouched: any = () => {};
  onKeyPress: any = () => true;

  constructor(
    @Self()
    @Optional()
    public ngControl: NgControl,
    public changeDetectorRef: ChangeDetectorRef,
    private readonly errorMessageService: ErrorMessagesService,
    private readonly translator: TranslatorService,
    private readonly configurationService: ConfigurationService
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.appearance =
      this.configurationService.getConfig()?.themeOptions?.matInputAppearance ||
      'fill';

    //random id for the hint and mat-error id and aria-describedby of the input
    this.errorId = randomId(8);
    this.hintId = randomId(8);
    this.labelId = 'input-text-' + randomId(8);
    this.helpTextId = randomId(8);
    this.autocompleteId = randomId(8);
  }

  ngAfterViewInit(): void {
    this.mainInputElement.nativeElement.removeAttribute('aria-haspopup');

    this.autocompleteId = this.autocompleteElement.id;

    this.setDisabled(this.disabled); // needed if there is no ngControl and disabled is set before initializing the input element
  }

  ngOnInit() {
    this.valueChangesSubscription = this.ngControl?.valueChanges?.subscribe(
      () => {
        this.changeDetectorRef.detectChanges();
      }
    );

    this.suggestionSearchSubscription = this.suggestionSearch$
      .pipe(debounce(() => interval(this.updateSuggestionDebounceTime)))
      .subscribe((value) => {
        this.updateSuggestions.emit(value);
      });

    this.showLeftIcon =
      !!this.value || this.appearance === 'outline' || this.forceShowLeftIcon;
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    this.valueChangesSubscription?.unsubscribe();
  }

  writeValue(value: string): void {
    this.value = value;
    this.suggestionSearch$.next(value);
    this.changeDetectorRef.detectChanges();

    // Sometimes, after a click on a suggestion, the native value is set to [object Object] even if this.value is a normal string
    const nativeInput = this.mainInputElement
      ?.nativeElement as HTMLInputElement;
    if (nativeInput && nativeInput?.value !== this.value) {
      nativeInput.value = this.value;
    }
  }

  registerOnKeyPress(fn: any): void {
    this.onKeyPress = fn;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.setDisabled(isDisabled);
  }

  setDisabled(newDisabled: boolean) {
    this._disabled = newDisabled;

    if (!this.ngControl && this.mainInputElement?.nativeElement) {
      this.mainInputElement.nativeElement.disabled = newDisabled;
    }
  }

  getInputTitle() {
    return this.translator.getTranslation(
      'GlobalApplicativeConstants.accessibility.inputTitle',
      {
        label: this.label,
        isRequired:
          this.hasRequiredValidator(this.ngControl?.control) &&
          !this.ngControl?.disabled &&
          !this.disabled,
        value: this.value || '',
        hasValue: !!this.value,
      }
    );
  }

  getAriaDescribedby() {
    const describerIdList = [];

    if (this.isError()) {
      describerIdList.push(this.errorId);
    }

    if (this.suggestions && !!this.value) {
      describerIdList.push(this.helpTextId);
      describerIdList.push(this.autocompleteId);
    }

    describerIdList.push(this.ariaDescribedby);

    const resultStr = describerIdList.join(' ');

    return resultStr.trim() || undefined;
  }

  getDataTestId() {
    return this.dataTestId || this.ngControl?.name || undefined;
  }

  getError() {
    if (!this.ngControl?.control) {
      return null;
    }

    return this.errorMessageService.getError(
      this.ngControl?.control,
      this.label,
      this.errorPattern
    );
  }

  displayError() {
    return !!this.getError() && this.isError();
  }

  isError() {
    return (
      (this.ngControl?.invalid &&
        (this.ngControl?.dirty || this.ngControl?.touched)) ||
      this.forceDisplayError
    );
  }

  onInputValueChange(event: any) {
    const { value } = event.target;

    this.value = value;
    this.suggestionSearch$.next(value);
    this.onChange(value);
  }

  onInputClick() {
    if (this.scrollToSuggestion) {
      const el = document.getElementById(
        `mat-option-${
          this.suggestionsLabelName
            ? this.scrollToSuggestion[this.suggestionsLabelName]
            : this.scrollToSuggestion
        }`
      );

      el?.scrollIntoView();
    }
  }

  onFocusin() {
    this.focus = true;
    this.showLeftIcon = true;
  }

  onFocusout() {
    this.focus = false;
    this.showLeftIcon =
      !!this.value || this.appearance === 'outline' || this.forceShowLeftIcon;
  }

  onOptionSelected(option: any) {
    this.writeValue(
      this.suggestionsLabelName ? option[this.suggestionsLabelName] : option
    );
    this.selectSuggestion.emit(option);
  }

  onRightIconClick(event) {
    if (this.clickableIcon && !this.disabled) {
      if (this.suggestions) {
        event.stopPropagation();
        this.autocompleteTriggerElement.openPanel();
      }
      this.rightIconClick.emit(this.value);
    }
  }

  onLeftIconClick() {
    if (this.clickableIcon && !this.disabled) {
      this.leftIconClick.emit(this.value);
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown($event: any) {
    if (
      $event?.srcElement?.nodeName === 'INPUT' &&
      $event?.key === 'Enter' &&
      this.forceOptionSelectedOnEnter &&
      this.suggestions?.length
    ) {
      this.autocompleteTriggerElement.closePanel();
      const selectedOption = this.autocompleteElement.options.find(
        (option) => option.selected
      );

      this.onOptionSelected(selectedOption?.value || this.suggestions[0]);
      $event.preventDefault();
    }
  }

  hasRequiredValidator(control: AbstractControl): boolean {
    return UtilsValidators.hasRequiredValidator(control);
  }

  getSuggestionsLabel(option) {
    return this.suggestionLabelFunc
      ? this.suggestionLabelFunc(option)
      : this.suggestionsLabelName
      ? option[this.suggestionsLabelName]
      : option;
  }
}
